import { Link } from '@tanstack/react-router';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { DrilldownLink } from '../drilldowns';

/**
 * Direction of a "good" change for this stat — drives the delta color.
 *
 *   - `up-is-good`: completion rate, completed-tasks count → green when ▲.
 *   - `down-is-good`: open tasks, age, anything that should shrink → green when ▼.
 *   - `neutral`: referrals count, active users — show the delta in plain
 *     muted text, no opinion on direction.
 */
export type DeltaPolarity = 'up-is-good' | 'down-is-good' | 'neutral';

/**
 * Generic single-stat tile body — a big number with an optional caption
 * and a prior-period delta (▲ X / ▼ Y vs prior 30d). Used by all four
 * stat tiles; composing them around the same primitive keeps visual
 * rhythm consistent across the dashboard.
 *
 * If `drilldown` is provided, the value renders as a TanStack Router
 * `<Link>` that navigates to the destination list page with filters
 * pre-applied. Hover styling is automatic.
 */
export function StatTile({
  value,
  caption,
  isLoading,
  isError,
  tone = 'default',
  drilldown,
  delta,
}: {
  value: number | string | null | undefined;
  caption?: string;
  isLoading?: boolean;
  isError?: boolean;
  tone?: 'default' | 'positive' | 'warning';
  drilldown?: DrilldownLink;
  /**
   * Optional ▲/▼ delta vs the prior period. The tile decides color from
   * `polarity` so the same number reads green on "completion rate up"
   * and green on "open tasks down."
   */
  delta?: {
    /** Signed change. Negative renders ▼, positive renders ▲, zero hides. */
    diff: number;
    /**
     * Optional formatter for the diff value — e.g. for percentage-point
     * changes use `(d) => `${Math.abs(d).toFixed(1)}pp`` so "▲ 2pp" reads
     * naturally for completion rate. Defaults to integer count.
     */
    format?: (diff: number) => string;
    polarity: DeltaPolarity;
    /** Window label, e.g. "vs prior 30d". */
    periodLabel: string;
  };
}): React.ReactElement {
  if (isLoading) {
    return (
      <div className="flex h-full flex-col justify-center gap-2">
        <Skeleton className="h-10 w-24" />
        {caption !== undefined && <Skeleton className="h-3 w-32" />}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Failed to load
      </div>
    );
  }

  const display = value ?? '—';
  const toneClass = tone === 'positive' ? 'text-success' : tone === 'warning' ? 'text-warning' : '';
  const valueClass = cn('text-3xl font-semibold tabular-nums', toneClass);

  const valueNode = drilldown ? (
    <Link
      to={drilldown.to}
      search={drilldown.search}
      title={drilldown.title}
      className={cn(
        valueClass,
        'inline-block rounded-sm underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
      )}
    >
      {display}
    </Link>
  ) : (
    <div className={valueClass}>{display}</div>
  );

  return (
    <div className="flex h-full flex-col justify-center">
      {valueNode}
      {delta && delta.diff !== 0 && <DeltaBadge {...delta} />}
      {caption && <div className="mt-1 text-xs text-muted-foreground">{caption}</div>}
    </div>
  );
}

/**
 * Renders the ▲/▼ delta line under a stat value. Color is decided by
 * `polarity` × sign-of-diff:
 *   diff > 0 + up-is-good      → green
 *   diff > 0 + down-is-good    → red
 *   diff < 0 + up-is-good      → red
 *   diff < 0 + down-is-good    → green
 *   neutral polarity           → muted regardless of sign
 */
function DeltaBadge({
  diff,
  format,
  polarity,
  periodLabel,
}: {
  diff: number;
  format?: (d: number) => string;
  polarity: DeltaPolarity;
  periodLabel: string;
}): React.ReactElement {
  const arrow = diff > 0 ? '▲' : '▼';
  const formatted = format ? format(diff) : String(Math.abs(Math.round(diff)));
  const isImproving =
    polarity === 'neutral'
      ? null
      : (polarity === 'up-is-good' && diff > 0) || (polarity === 'down-is-good' && diff < 0);
  const colorClass =
    isImproving === null
      ? 'text-muted-foreground'
      : isImproving
        ? 'text-success'
        : 'text-destructive';
  return (
    <div className={cn('mt-1 text-xs font-medium', colorClass)}>
      {arrow} {formatted} <span className="text-muted-foreground">{periodLabel}</span>
    </div>
  );
}
